import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { X, Loader2, ClipboardCheck, ChevronRight, CheckCircle2, XCircle, Sparkles } from 'lucide-react';

const STATUS_STYLE = {
  open: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30',
  ai_reviewed: 'text-sky-700 dark:text-sky-300 bg-sky-500/10 border-sky-500/30',
  applied: 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  dismissed: 'text-muted-foreground bg-muted/40 border-border/40',
};
const CAT_LABEL = {
  wrong_measure: 'Wrong measure/field', wrong_calculation: 'Incorrect calculation',
  bad_join: 'Bad multi-table join', misrouted: 'Question mis-routed',
  stale_cache: 'Stale cached answer', model_definition_gap: 'Model definition gap',
  permissions_pii: 'Permissions / PII', working_as_intended: 'Working as intended',
};

// Admin/steward review queue for reported conversations. Reads AI diagnoses and
// lets a reviewer apply a corrected query (→ SQL Library) or dismiss the case.
export function ReviewQueuePanel({ modelId, onClose }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCase, setOpenCase] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.listReviewCases(modelId).then((r) => setCases(r?.cases || [])).catch(() => setCases([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [modelId]);

  // Auto-refresh while a case is still being reviewed (background AI).
  useEffect(() => {
    if (!cases.some((c) => c.status === 'open')) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [cases]);

  const openDetail = async (caseId) => {
    try { setOpenCase(await api.getReviewCase(caseId)); } catch (e) { toast.error(e.message); }
  };

  const applyFix = async (idx) => {
    setBusy(true);
    try {
      await api.applyReviewFix(openCase.case_id, idx);
      toast.success('Fix applied — corrected query published to the SQL Library.');
      const fresh = await api.getReviewCase(openCase.case_id);
      setOpenCase(fresh); load();
    } catch (e) { toast.error(e.message || 'Could not apply the fix.'); }
    finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.dismissReviewCase(openCase.case_id, '');
      toast.success('Case dismissed.');
      setOpenCase(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border/60 rounded-2xl shadow-xl overflow-hidden" data-testid="review-queue-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/20">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Reported conversations — review queue</span>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="review-queue-close"><X className="w-4 h-4" /></button>
      </div>

      <div className="max-h-[70vh] overflow-auto">
        {loading && <div className="p-6 text-center text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>}

        {!loading && !openCase && cases.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">No reported conversations yet. Use “Report a problem” in a conversation to file one.</div>
        )}

        {!loading && !openCase && cases.map((c) => (
          <button key={c.case_id} onClick={() => openDetail(c.case_id)} className="w-full text-left px-4 py-3 border-b border-border/30 hover:bg-muted/30 flex items-start gap-2" data-testid={`review-case-${c.case_id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${STATUS_STYLE[c.status] || ''}`}>
                  {c.status === 'ai_reviewed' ? 'AI-reviewed' : c.status === 'open' ? 'Reviewing…' : c.status}
                </span>
                <span className="text-[10px] text-muted-foreground">{c.reported_by_email}</span>
              </div>
              <div className="text-xs text-foreground mt-1 truncate">{c.comment}</div>
              {c.status === 'ai_reviewed' && <div className="text-[10px] text-muted-foreground mt-0.5">{(c.findings || []).length} finding(s)</div>}
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground mt-1 shrink-0" />
          </button>
        ))}

        {openCase && (
          <div className="p-4 space-y-3">
            <button onClick={() => setOpenCase(null)} className="text-[11px] text-primary hover:underline">← Back to queue</button>
            <div className="text-xs">
              <span className="text-muted-foreground">Reported by </span><b>{openCase.reported_by_email}</b>
            </div>
            <div className="rounded-lg bg-muted/30 border border-border/40 p-2.5 text-xs text-foreground"><b>Complaint:</b> {openCase.comment}</div>

            {openCase.status === 'open' && (
              <div className="p-4 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> AI is reviewing this conversation…</div>
            )}

            {openCase.ai_summary && (
              <div className="rounded-lg bg-sky-500/[0.06] border border-sky-500/25 p-2.5 text-xs text-sky-900 dark:text-sky-200 flex items-start gap-1.5">
                <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span><b>AI verdict:</b> {openCase.ai_summary}</span>
              </div>
            )}

            {(openCase.findings || []).map((f, i) => (
              <div key={i} className="rounded-lg border border-border/50 p-3 space-y-2" data-testid={`review-finding-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted/60 text-foreground/80 font-medium">Turn {f.turn}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-border/50 text-muted-foreground">{CAT_LABEL[f.category] || f.category}</span>
                  {f.status === 'applied' && <span className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> applied</span>}
                </div>
                <div className="text-xs text-foreground"><b>Why:</b> {f.root_cause}</div>
                {f.fix_type === 'corrected_query' && f.corrected_sql && (
                  <pre className="font-mono text-[10px] text-sky-300/80 bg-[#0d1117] p-2 rounded-lg overflow-auto max-h-40 whitespace-pre-wrap">{f.corrected_sql}</pre>
                )}
                {f.fix_type !== 'corrected_query' && f.recommendation && (
                  <div className="text-[11px] text-muted-foreground"><b>Recommendation:</b> {f.recommendation}</div>
                )}
                {f.status !== 'applied' && f.fix_type === 'corrected_query' && (
                  <button onClick={() => applyFix(i)} disabled={busy} className="text-[11px] px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50 flex items-center gap-1" data-testid={`review-apply-${i}`}>
                    <CheckCircle2 className="w-3 h-3" /> Apply — publish corrected query to SQL Library
                  </button>
                )}
              </div>
            ))}

            {openCase.status !== 'dismissed' && (
              <div className="flex justify-end pt-1">
                <button onClick={dismiss} disabled={busy} className="text-[11px] px-2.5 py-1 rounded-lg border border-border/50 text-muted-foreground hover:bg-muted/40 flex items-center gap-1" data-testid="review-dismiss-btn">
                  <XCircle className="w-3 h-3" /> Dismiss case
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
